import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  inject,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute } from '@angular/router';
import { combineLatest, from, Observable, Subject } from 'rxjs';
import { filter, map, takeUntil, withLatestFrom } from 'rxjs/operators';
import { NoticeActionDto, ProcedureOverviewDto } from '../../api/notices';
import { GroupModel } from '../../api/user-service';
import { GruppenService } from '../../gruppen/gruppen.service';
import { NoticeOverviewViewModel } from '../NoticeOverviewViewModel';
import { BekanntmachungenVerwaltungStateService } from './bekanntmachungen-verwaltung-state.service';
import { UserSettingsStateService } from '../../shared/user-settings-state.service';
import { KeycloakService } from 'keycloak-angular';
import { KeycloakProfile } from 'keycloak-js';

@Component({
  selector: 'app-bekanntmachungen-verwaltung',
  templateUrl: './bekanntmachungen-verwaltung.component.html',
  styleUrls: ['./bekanntmachungen-verwaltung.component.scss'],
})
export class BekanntmachungenVerwaltungComponent implements OnInit, AfterViewInit, OnDestroy {

  bekanntmachungenLoading$: Observable<boolean>;
  userAndGroupData$: Observable<{ loggedInUser: KeycloakProfile; groups: GroupModel[] }>;
  bekanntmachungenAndUsersettingsLoading$: Observable<boolean>
  groups$: Observable<GroupModel[]>;
  filter$: Observable<{ showFavorites: boolean; showHidden: boolean; }>;

  isFirstPage: boolean = true;
  pageSizeOptions: number[] = [10, 25, 50];

  dataSource = new MatTableDataSource<ProcedureOverviewDto>();

  @ViewChildren(MatPaginator) paginators: QueryList<MatPaginator>;

  private bekanntmachungState = inject(BekanntmachungenVerwaltungStateService);
  private userSettingsState = inject(UserSettingsStateService);
  private gruppenService = inject(GruppenService);
  private cdr = inject(ChangeDetectorRef);
  private activatedRoute = inject(ActivatedRoute);
  private keycloak = inject(KeycloakService);

  private destroy$: Subject<void> = new Subject();

  ngOnInit(): void {
    this.bekanntmachungenLoading$ = this.bekanntmachungState.loading$;
    this.groups$ = this.gruppenService.groups$;
    this.filter$ = this.bekanntmachungState.filter$;
    this.userAndGroupData$ = combineLatest({
      loggedInUser: from(this.keycloak.loadUserProfile()),
      groups: this.groups$,
    });
    this.bekanntmachungenAndUsersettingsLoading$ = combineLatest([this.userSettingsState.isInitialized$, this.bekanntmachungenLoading$])
      .pipe(map(([isInitialized, isLoading]) => !isInitialized || isLoading));

    this.bekanntmachungState.procedures$.pipe(takeUntil(this.destroy$)).subscribe(procedures => this.dataSource.data = procedures);

    // take actions when specific properties change
    combineLatest({
      filter: this.filter$,
      favorites: this.userSettingsState.favoriteProcedures$,
      hidden: this.userSettingsState.hiddenProcedures$,
    }).pipe(
      takeUntil(this.destroy$),
    ).subscribe(({ filter, favorites, hidden }) => {
      this.dataSource.filterPredicate = (procedure) =>
        (!filter.showFavorites || favorites.includes(procedure.id)) &&
        (filter.showHidden || !hidden.includes(procedure.id));
      this.dataSource.filter = 'dummy';
    });

    // trigger loading everything
    this.bekanntmachungState.init();
    this.gruppenService.loadGroupsAndInvitations();
  }

  ngAfterViewInit() {
    // initialize paginator and connect it to dataSource only right after it is really visible in the UI
    this.paginators.changes.pipe(
      filter((elementList: QueryList<MatPaginator>) => elementList.length > 0),
      map((elementList: QueryList<MatPaginator>) => elementList.get(0)),
      withLatestFrom(this.userSettingsState.maxProceduresPerPage$),
      takeUntil(this.destroy$),
    ).subscribe(([paginator, pageSize]: [MatPaginator, number]) => {
      this.dataSource.paginator = paginator;

      paginator._changePageSize(pageSize);
      paginator.firstPage();
      this.cdr.detectChanges();
    });
  }

  userFilterChanged(userId: string): void {
    this.bekanntmachungState.selectUser(userId);
  }

  favoritesFilterChanged(event: MatCheckboxChange): void {
    this.bekanntmachungState.updateFavoriteFilter(event.checked);
  }

  hiddenFilterChanged(event: MatCheckboxChange): void {
    this.bekanntmachungState.updateHiddenFilter(event.checked);
  }

  toggleFavorite($event: MouseEvent, procedure: ProcedureOverviewDto): void {
    $event.stopPropagation();
    this.userSettingsState.updateFavoriteProcedures(procedure.id).subscribe();
  }

  toggleHidden($event: MouseEvent, procedure: ProcedureOverviewDto): void {
    $event.stopPropagation();
    this.userSettingsState.updateHiddenProcedures(procedure.id).subscribe();
  }

  onPageEvent(event: PageEvent) {
    this.userSettingsState.updatePageSize(event.pageSize).subscribe();
    this.isFirstPage = event.pageIndex === 0;

    window.scrollTo(0, 0);
    // If scrolling didn't work (because of changes to the DOM, try again in 100ms)
    setTimeout(() => {
      if (window.screenY < 100) window.scrollTo(0, 0);
    }, 100);
  }

  onClickActionType({ action, subTypeId, notice }: {
    action: NoticeActionDto;
    subTypeId?: string;
    notice: NoticeOverviewViewModel
  }): void {
    switch (action.type) {
      case NoticeActionDto.TypeEnum.EditDraft:
        this.bekanntmachungState.editDraft(action, notice, this.activatedRoute);
        break;
      case NoticeActionDto.TypeEnum.DeleteDraft:
        this.bekanntmachungState.deleteDraft(notice);
        break;
      case NoticeActionDto.TypeEnum.ShowPreview:
        this.bekanntmachungState.showPreview(notice);
        break;
      case NoticeActionDto.TypeEnum.ExportCn:
        this.bekanntmachungState.exportNotice(notice);
        break;
      case NoticeActionDto.TypeEnum.Stop:
        this.bekanntmachungState.stop(notice);
        break;
      case NoticeActionDto.TypeEnum.CreateTemplate:
        this.bekanntmachungState.createTemplate(notice);
        break;
      case NoticeActionDto.TypeEnum.EditSentNotice:
      case NoticeActionDto.TypeEnum.EditSentChangeNotice:
      case NoticeActionDto.TypeEnum.CreateChangeNotice:
        this.bekanntmachungState.createUpdateOrSuccessor(action, notice, notice.notice.subTypeId, this.activatedRoute);
        break;
      case NoticeActionDto.TypeEnum.CreateCn:
      case NoticeActionDto.TypeEnum.CreateCan:
      case NoticeActionDto.TypeEnum.CreateCanModif:
        this.bekanntmachungState.createUpdateOrSuccessor(action, notice, subTypeId, this.activatedRoute);
        break;
      default:
        throw new Error('Keine gültige Aktion.');
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
